import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import {
  getKnowledgeRegistryCookieSyncWarning,
  knowledgeRegistrySyncBannerText,
} from "@/lib/knowledgeRegistryCrossSync";
import { knowledgeOverviewPath } from "@/lib/knowledgePaths";
import { KnowledgeBaseSwitcher } from "@/pages/knowledge/KnowledgeBaseSwitcher";

export function KnowledgeChrome({
  title,
  description,
  badge,
  actions,
  children,
  showBaseSwitcher = true,
  backHref,
  backLabel = "Back",
}: {
  title: string;
  description: string;
  badge?: string;
  actions?: ReactNode;
  children: ReactNode;
  /** Hide on the Knowledge base overview, which manages bases itself. */
  showBaseSwitcher?: boolean;
  /** Optional back control shown before the page title. */
  backHref?: string;
  backLabel?: string;
}) {
  const syncFailed = Boolean(getKnowledgeRegistryCookieSyncWarning());
  const syncBanner = syncFailed ? knowledgeRegistrySyncBannerText() : null;

  return (
    <div className="space-y-6 p-6 sm:p-8">
      {syncBanner ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
          {syncBanner}
        </p>
      ) : null}
      {showBaseSwitcher ? <KnowledgeBaseSwitcher /> : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {backHref ? (
              <Link
                to={backHref}
                aria-label={backLabel}
                title={backLabel}
                className="inline-flex size-9 items-center justify-center rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-white/20 dark:text-slate-200 dark:hover:bg-white/5"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
              </Link>
            ) : null}
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{title}</h1>
            {badge ? (
              <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-white/10 dark:text-slate-300">
                {badge}
              </span>
            ) : null}
          </div>
          <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">{description}</p>
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}

/** Shown on Libraries, Books, and Articles when the org has no knowledge base yet. */
export function KnowledgeNeedsBase({
  title = "Create a knowledge base first",
  detail = "Libraries, books, and articles are scoped to a knowledge base. Your org starts with none. Create the first one, then come back here.",
}: {
  title?: string;
  detail?: string;
}) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center dark:border-white/15 dark:bg-slate-900/40">
      <h2 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h2>
      <p className="mx-auto mt-2 max-w-lg text-sm text-slate-600 dark:text-slate-400">{detail}</p>
      <Link
        to={knowledgeOverviewPath()}
        className="mt-5 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
      >
        <Plus className="h-4 w-4" aria-hidden />
        New knowledge base
      </Link>
    </div>
  );
}

export function KnowledgeError({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{message}</div>
  );
}

export const knowledgePrimaryLinkClass =
  "inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500";

export const knowledgeSecondaryLinkClass =
  "inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 dark:border-white/15 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800";

export const knowledgeTableWrapClass =
  "overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40";

export function KnowledgeBackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300">
      {children}
    </Link>
  );
}
